import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { SkeletonRows } from "../../Components/Common/LoadingStates";
import {
  getFormSubmissions,
  getFormSubmissionById,
  patchFormSubmission,
  archiveFormSubmission,
  unarchiveFormSubmission,
  deleteFormSubmission,
  getFormSubmissionStats,
  downloadFormSubmissionCsv,
} from "../../helpers/fakebackend_helper";
import { useCachedRequest, invalidateCache } from "../../helpers/cachedFetch";
import { usePermission } from "../../helpers/usePermission";
import ListingForm from "../TokenListings/components/ListingForm";
import { submissionToForm } from "../TokenListings/utils";

const TYPES = [
  { value: "token", label: "Token Listing", color: "#00CD52" },
  { value: "integration", label: "Direct Integration", color: "#7B7CFF" },
  { value: "collab", label: "Partnership", color: "#f3af4c" },
  { value: "dapp", label: "dApp Submission", color: "#5DDFFF" },
  { value: "general", label: "General Inquiry", color: "#9CA3AF" },
];

const STATUSES = [
  { value: "new", label: "New", color: "#7B7CFF" },
  { value: "reviewing", label: "Reviewing", color: "#f3af4c" },
  { value: "done", label: "Done", color: "#00CD52" },
  { value: "spam", label: "Spam", color: "#ff6e6e" },
];

const STATUS_COLOR = Object.fromEntries(STATUSES.map((s) => [s.value, s.color]));
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.value, s.label]));
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.value, t.label]));
const TYPE_COLOR = Object.fromEntries(TYPES.map((t) => [t.value, t.color]));

const PRIMARY_NAME_KEYS = ["projectName", "orgName", "dappName", "contactName", "topic"];
const SUBLINE_KEYS = ["contactEmail", "telegram", "telegramHandle", "website"];
const URL_REGEX = /^https?:\/\//i;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HEX_REGEX = /^0x[a-fA-F0-9]{40,}$/;

// Three distinct buckets + All. Archive = "maybe later"; Spam = scammers/trash.
const ARCHIVED_FILTERS = [
  { value: "active", label: "Active" },
  { value: "archive", label: "Archive" },
  { value: "spam", label: "Spam" },
  { value: "all", label: "All" },
];

const ATTACHMENT_BADGE = {
  pdf: "PDF",
  png: "PNG",
  jpg: "JPG",
  jpeg: "JPG",
  webp: "WEBP",
  heic: "HEIC",
  heif: "HEIC",
};

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const extOf = (name) => {
  if (typeof name !== "string") return "";
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : "";
};

const firstNonEmpty = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === "string" && v.trim()) return v.trim();
    if (typeof v === "number" || typeof v === "boolean") return String(v);
  }
  return null;
};

const labelizeKey = (key) => {
  if (!key) return "";
  const spaced = key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
};

const formatRelative = (iso) => {
  if (!iso) return "-";
  try {
    const now = Date.now();
    const t = new Date(iso).getTime();
    const diff = Math.max(0, now - t);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return formatDate(iso);
  } catch (_) {
    return iso;
  }
};

const StatusChip = ({ status }) => {
  const color = STATUS_COLOR[status] || "#888";
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium"
      style={{ backgroundColor: `${color}22`, color }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
      {STATUS_LABEL[status] || status}
    </span>
  );
};

const TypeChip = ({ type }) => (
  <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-gray-700 text-gray-200">
    {TYPE_LABEL[type] || type}
  </span>
);

const renderValue = (value) => {
  if (value === null || value === undefined || value === "") return <span className="text-gray-400">—</span>;
  if (typeof value === "boolean") return <span>{value ? "Yes" : "No"}</span>;
  if (typeof value === "number") return <span className="tabular-nums">{value}</span>;
  if (typeof value === "object") {
    // Arrays, nested objects — shouldn't happen with current backend validation,
    // but legacy rows might have them. Render as formatted JSON rather than
    // the useless "[object Object]" fallback.
    let rendered;
    try {
      rendered = JSON.stringify(value, null, 2);
    } catch (_) {
      rendered = String(value);
    }
    return (
      <pre className="font-mono text-xs whitespace-pre-wrap break-words bg-bl-accent/5 border border-gray-700 px-2 py-1.5 rounded max-h-64 overflow-auto">
        {rendered}
      </pre>
    );
  }
  const str = String(value);
  if (HEX_REGEX.test(str.trim())) {
    return <span className="font-mono text-xs break-all">{str}</span>;
  }
  if (URL_REGEX.test(str.trim())) {
    return (
      <a href={str.trim()} target="_blank" rel="noreferrer" className="text-bl-accent-light hover:underline break-all">
        {str}
      </a>
    );
  }
  if (EMAIL_REGEX.test(str.trim())) {
    return (
      <a href={`mailto:${str.trim()}`} className="text-bl-accent-light hover:underline break-all">
        {str}
      </a>
    );
  }
  return <span className="whitespace-pre-wrap break-words">{str}</span>;
};

const useDebounced = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const FormSubmissions = ({ embedded = false } = {}) => {
  document.title = "Form Submissions | BiorGov - BiorGov Admin & Dashboard";
  const can = usePermission();
  const canUpdate = can("form_submissions.update");
  const canDelete = can("form_submissions.delete");
  const canCreateListing = can("token_listings.manage");
  // Prefilled token listing form opened from a "token" submission: { form, hints }.
  const [listingPrefill, setListingPrefill] = useState(null);
  // While the listing form is open on top, the drawer must not react to keys
  // (its ESC and focus trap would otherwise fight the form's own).
  const listingOpenRef = useRef(false);
  listingOpenRef.current = Boolean(listingPrefill);

  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [archivedFilter, setArchivedFilter] = useState("active");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 300);

  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftStatus, setDraftStatus] = useState("new");
  const [draftNotes, setDraftNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadingDetailId, setLoadingDetailId] = useState(null);
  const toastTimer = useRef(null);
  const drawerContentRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);
  const listReqIdRef = useRef(0);
  const clearSelectedTimerRef = useRef(null);

  const showToast = useCallback((kind, text) => {
    setToast({ kind, text });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  const listReq = useCachedRequest({
    key: `form-submissions:list:${typeFilter}:${statusFilter}:${archivedFilter}:${debouncedSearch.trim()}`,
    fetcher: () => {
      const params = { limit: 200, offset: 0, sort: "newest" };
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      params.bucket = archivedFilter; // active | archive | spam | all
      // axios interceptor already unwraps `.data` — `res` IS the JSON body.
      return getFormSubmissions(params);
    },
    deps: [typeFilter, statusFilter, archivedFilter, debouncedSearch],
  });
  const items = Array.isArray(listReq.data?.items) ? listReq.data.items : [];
  const total = listReq.data?.total || 0;
  const loading = listReq.loading;

  const statsReq = useCachedRequest({
    key: "form-submissions:stats",
    fetcher: () => getFormSubmissionStats(),
  });
  const stats = statsReq.data;

  useEffect(() => {
    if (listReq.error && !listReq.data) {
      setError(typeof listReq.error === "string" ? listReq.error : listReq.error?.message || "Failed to load submissions");
    }
  }, [listReq.error, listReq.data]);

  const fetchList = useCallback(() => {
    invalidateCache("form-submissions:list:");
    return listReq.refetch();
  }, [listReq]);
  const fetchStats = useCallback(() => {
    invalidateCache("form-submissions:stats");
    return statsReq.refetch();
  }, [statsReq]);

  // Defined BEFORE the focus-management useEffect because that effect's
  // dependency array references closeDrawer — a temporal-dead-zone error
  // if it were declared later.
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    if (clearSelectedTimerRef.current) clearTimeout(clearSelectedTimerRef.current);
    clearSelectedTimerRef.current = setTimeout(() => {
      setSelected(null);
      clearSelectedTimerRef.current = null;
    }, 200);
  }, []);

  const openDetail = async (id) => {
    if (loadingDetailId) return;
    if (clearSelectedTimerRef.current) {
      clearTimeout(clearSelectedTimerRef.current);
      clearSelectedTimerRef.current = null;
    }
    setLoadingDetailId(id);
    try {
      const payload = await getFormSubmissionById(id);
      setSelected(payload);
      setDraftStatus(payload?.status || "new");
      setDraftNotes(payload?.notes || "");
      setDrawerOpen(true);
    } catch (err) {
      showToast("error", typeof err === "string" ? err : err?.message || "Failed to load submission");
    } finally {
      setLoadingDetailId(null);
    }
  };

  // Drawer accessibility: remember what was focused before opening, move focus
  // to the close button when opening, restore focus when closing. ESC closes.
  // Tab cycles within the drawer so focus can't escape to the background list.
  useEffect(() => {
    if (!drawerOpen) {
      const prev = previousFocusRef.current;
      previousFocusRef.current = null;
      if (prev && typeof prev.focus === "function") {
        try { prev.focus(); } catch (_) { /* element may be gone */ }
      }
      return undefined;
    }

    previousFocusRef.current = document.activeElement;
    const focusTimer = window.setTimeout(() => closeButtonRef.current?.focus(), 0);

    const FOCUSABLE_SELECTOR =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const onKeyDown = (e) => {
      if (listingOpenRef.current) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        closeDrawer();
        return;
      }
      if (e.key !== "Tab" || !drawerContentRef.current) return;
      const nodes = drawerContentRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
      if (nodes.length === 0) {
        e.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !drawerContentRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !drawerContentRef.current.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [drawerOpen, closeDrawer]);

  const handleSave = async () => {
    if (!selected) return;
    if (draftNotes.length > 20000) {
      showToast("error", "Notes exceed 20,000 characters");
      return;
    }
    setSaving(true);
    try {
      const body = {};
      if (draftStatus !== selected.status) body.status = draftStatus;
      if (draftNotes !== (selected.notes || "")) body.notes = draftNotes;
      if (Object.keys(body).length === 0) {
        showToast("info", "No changes to save");
        setSaving(false);
        return;
      }
      const updated = await patchFormSubmission(selected.id, body);
      setSelected(updated);
      setDraftStatus(updated?.status || "new");
      setDraftNotes(updated?.notes || "");
      await fetchList();
      await fetchStats();
      showToast("success", "Saved");
    } catch (err) {
      showToast("error", typeof err === "string" ? err : err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveToggle = async () => {
    if (!selected) return;
    const isArchived = Boolean(selected.archivedAt);
    setArchiving(true);
    try {
      const updated = isArchived
        ? await unarchiveFormSubmission(selected.id)
        : await archiveFormSubmission(selected.id);
      setSelected(updated);
      setDraftStatus(updated?.status || draftStatus);
      setDraftNotes(updated?.notes || draftNotes);
      await fetchList();
      await fetchStats();
      showToast("success", isArchived ? "Unarchived" : "Archived");
    } catch (err) {
      showToast(
        "error",
        typeof err === "string" ? err : err?.message || (isArchived ? "Failed to unarchive" : "Failed to archive"),
      );
    } finally {
      setArchiving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete submission ${selected.id}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deleteFormSubmission(selected.id);
      closeDrawer();
      await fetchList();
      await fetchStats();
      showToast("success", "Deleted");
    } catch (err) {
      showToast("error", typeof err === "string" ? err : err?.message || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadFormSubmissionCsv(typeFilter || undefined, archivedFilter);
    } catch (err) {
      showToast("error", typeof err === "string" ? err : err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const selectedDataEntries = useMemo(() => {
    if (!selected?.data) return [];
    return Object.entries(selected.data);
  }, [selected]);

  return (
    <div className={embedded ? "min-w-0" : "min-h-screen min-w-0"}>
      <div className={embedded ? "w-full min-w-0" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full min-w-0"}>
        {!embedded && <BreadCrumb title="Form Submissions" pageTitle="Marketing" />}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <StatCard label="Active" value={stats?.active ?? "—"} />
          {STATUSES.map((s) => (
            <StatCard
              key={s.value}
              label={s.label}
              value={stats?.byStatus?.[s.value] ?? "—"}
              color={s.color}
            />
          ))}
          <StatCard label="Archived" value={stats?.archived ?? "—"} />
        </div>

        {/* Filters */}
        <div className="bl-glass p-4 mb-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-gray-400 mb-1">Search</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search id, notes, fields…"
                className="w-full px-3 py-2 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-400"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-400"
              >
                <option value="">All types</option>
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                    {stats?.byType?.[t.value] !== undefined ? ` (${stats.byType[t.value]})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-400"
              >
                <option value="">All statuses</option>
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                    {stats?.byStatus?.[s.value] !== undefined ? ` (${stats.byStatus[s.value]})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">View</label>
              <select
                value={archivedFilter}
                onChange={(e) => setArchivedFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-400"
              >
                {ARCHIVED_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                    {f.value === "archive" && stats?.archived !== undefined ? ` (${stats.archived})` : ""}
                    {f.value === "spam" && stats?.byStatus?.spam !== undefined ? ` (${stats.byStatus.spam})` : ""}
                    {f.value === "all" && stats?.total !== undefined ? ` (${stats.total})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="px-4 py-2 text-sm border border-gray-700 rounded-md text-gray-200 hover:bg-bl-accent/10 transition-colors disabled:opacity-50"
            >
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
          </div>
        </div>

        {/* List */}
        <div className="bl-glass overflow-hidden">
          {error && (
            <div className="p-4 border-b border-red-500/30 bg-red-500/10 text-sm text-red-400">
              {error}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-bl-accent/5 border-b border-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Submitted</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {loading && items.length === 0 && (
                  <SkeletonRows rows={6} cols={4} />
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-12 text-center text-sm text-gray-400">
                      No submissions found
                    </td>
                  </tr>
                )}
                {items.map((item) => {
                  const name = firstNonEmpty(item.data, PRIMARY_NAME_KEYS) || `(${item.id})`;
                  const sub = SUBLINE_KEYS
                    .map((k) => item.data?.[k])
                    .filter((v) => typeof v === "string" && v.trim())
                    .join(" · ");
                  return (
                    <tr
                      key={item.id}
                      onClick={() => openDetail(item.id)}
                      className={`hover:bg-bl-accent/10 ${
                        loadingDetailId
                          ? "cursor-wait pointer-events-none opacity-60"
                          : "cursor-pointer"
                      } ${loadingDetailId === item.id ? "bg-bl-accent/10" : ""}`}
                    >
                      <td className="px-4 py-3 text-sm text-gray-100 whitespace-nowrap">
                        <div>{formatRelative(item.submittedAt)}</div>
                        <div className="text-xs text-gray-400">{formatDate(item.submittedAt)}</div>
                      </td>
                      <td className="px-4 py-3 text-sm max-w-[360px]">
                        <div className="text-gray-100 font-medium truncate" title={name}>{name}</div>
                        {sub && <div className="text-xs text-gray-400 truncate" title={sub}>{sub}</div>}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <TypeChip type={item.type} />
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <StatusChip status={item.status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!loading && total > items.length && (
            <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-700">
              Showing {items.length} of {total}
            </div>
          )}
        </div>
      </div>

      {/* Offcanvas drawer */}
      <div
        className={`fixed inset-0 z-[1000] transition-opacity ${drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!drawerOpen}
      >
        <div
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={closeDrawer}
        />
        <div
          ref={drawerContentRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="submission-drawer-title"
          className={`absolute top-0 right-0 h-full w-full max-w-3xl overflow-y-auto transform transition-transform duration-200 ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
          style={{
            backgroundColor: "var(--bl-bg)",
            borderLeft: selected ? `3px solid ${TYPE_COLOR[selected.type] || "var(--bl-accent)"}` : "1px solid var(--bl-line)",
            boxShadow: "-12px 0 40px rgba(0,0,0,0.5)",
          }}
        >
          {selected && (
            <div className="flex flex-col min-h-full">
              {/* Sticky header */}
              <div
                className="sticky top-0 z-10 px-6 py-5 flex items-start justify-between gap-3"
                style={{
                  background: "rgba(14, 13, 41, 0.92)",
                  backdropFilter: "blur(14px)",
                  WebkitBackdropFilter: "blur(14px)",
                  borderBottom: "1px solid var(--bl-line)",
                }}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap text-xs">
                    <span
                      className="font-semibold uppercase tracking-wider"
                      style={{ color: TYPE_COLOR[selected.type] || "#9CA3AF" }}
                    >
                      {TYPE_LABEL[selected.type] || selected.type}
                    </span>
                    <span className="text-gray-600">·</span>
                    <StatusChip status={selected.status} />
                    {selected.archivedAt ? (
                      <>
                        <span className="text-gray-600">·</span>
                        <span className="inline-flex items-center gap-1 text-gray-300">
                          <i className="ri-archive-line"></i>
                          Archived
                        </span>
                      </>
                    ) : null}
                  </div>
                  <h3
                    id="submission-drawer-title"
                    className="text-xl font-medium text-gray-100 break-words leading-tight"
                  >
                    {firstNonEmpty(selected.data, PRIMARY_NAME_KEYS) || `Submission ${selected.id}`}
                  </h3>
                  <div className="text-xs text-gray-500 mt-1.5 flex items-center gap-2 flex-wrap">
                    <span className="font-mono">{selected.id}</span>
                    <span>·</span>
                    <span>{formatRelative(selected.submittedAt)}</span>
                  </div>
                </div>
                <button
                  ref={closeButtonRef}
                  onClick={closeDrawer}
                  className="text-gray-400 hover:text-gray-100 hover:bg-white/5 rounded-lg p-2 transition-colors shrink-0"
                  aria-label="Close"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-6 space-y-5 flex-1">
                {/* Submitted information */}
                <section className="bl-glass p-6">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                    Submitted information
                  </h4>
                  {selectedDataEntries.length === 0 ? (
                    <p className="text-sm text-gray-400">No fields were submitted.</p>
                  ) : (
                    <dl className="grid grid-cols-1 gap-y-4">
                      {selectedDataEntries.map(([k, v]) => (
                        <div key={k}>
                          <dt className="text-[0.7rem] font-medium text-gray-400 uppercase tracking-wider mb-1">
                            {labelizeKey(k)}
                          </dt>
                          <dd className="text-sm text-gray-100">{renderValue(v)}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </section>

                {/* Attachments */}
                {Array.isArray(selected.attachments) && selected.attachments.length > 0 ? (
                  <section className="bl-glass p-6">
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                      Attachments
                    </h4>
                    <ul className="space-y-2">
                      {selected.attachments.map((att, i) => {
                        const ext = extOf(att?.name || "");
                        const badge = ATTACHMENT_BADGE[ext] || "FILE";
                        const rawHref = typeof att?.url === "string" ? att.url.trim() : "";
                        const safeHref = /^https?:\/\//i.test(rawHref) ? rawHref : "";
                        return (
                          <li
                            key={`${att?.key || att?.name || i}-${i}`}
                            className="flex items-center gap-3 p-3 rounded-lg border border-white/5 bg-white/[0.02]"
                          >
                            <span className="shrink-0 inline-flex items-center justify-center min-w-[2.6rem] h-6 px-1.5 rounded text-[0.66rem] font-semibold tracking-wider bg-bl-accent/16 text-bl-accent-light">
                              {badge}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-gray-100 truncate" title={att?.name || ""}>
                                {att?.name || att?.key || `Attachment ${i + 1}`}
                              </div>
                              <div className="text-xs text-gray-400 truncate" title={att?.mimeType || ""}>
                                {formatBytes(att?.size) || "—"}
                                {att?.mimeType ? ` · ${att.mimeType}` : ""}
                              </div>
                            </div>
                            {safeHref ? (
                              <a
                                href={safeHref}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="shrink-0 px-3 py-1.5 text-xs font-medium rounded-md border border-white/10 text-gray-200 hover:bg-bl-accent/10 hover:border-bl-accent/40 transition-colors"
                              >
                                Download
                              </a>
                            ) : (
                              <span
                                className="shrink-0 px-3 py-1.5 text-xs rounded-md border border-white/5 text-gray-500"
                                title="No accessible URL"
                              >
                                Unavailable
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ) : null}

                {/* Triage — write controls; hidden for view-only roles. */}
                {canUpdate && (
                <section className="bl-glass p-6">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                    Triage
                  </h4>
                  <div className="space-y-5">
                    <div>
                      <label className="block text-[0.7rem] font-medium text-gray-400 uppercase tracking-wider mb-2">
                        Status
                      </label>
                      <div className="flex gap-2 flex-wrap">
                        {STATUSES.map((s) => {
                          const active = draftStatus === s.value;
                          return (
                            <button
                              key={s.value}
                              type="button"
                              onClick={() => setDraftStatus(s.value)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all"
                              style={
                                active
                                  ? {
                                      backgroundColor: `${s.color}22`,
                                      color: s.color,
                                      border: `1px solid ${s.color}66`,
                                    }
                                  : {
                                      backgroundColor: "transparent",
                                      color: "#9CA3AF",
                                      border: "1px solid rgba(255,255,255,0.09)",
                                    }
                              }
                            >
                              <span
                                className="w-1.5 h-1.5 rounded-full"
                                style={{ backgroundColor: s.color }}
                              />
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <label className="block text-[0.7rem] font-medium text-gray-400 uppercase tracking-wider mb-2">
                        Notes
                        <span className="ml-2 text-gray-500 font-normal normal-case tracking-normal">
                          ({draftNotes.length} / 20000)
                        </span>
                      </label>
                      <textarea
                        value={draftNotes}
                        onChange={(e) => setDraftNotes(e.target.value)}
                        rows={6}
                        maxLength={20000}
                        className="w-full px-3 py-2.5 text-sm border border-white/10 rounded-lg bg-white/[0.03] text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent/60 focus:border-bl-accent/60 transition-colors resize-y"
                        placeholder="Internal notes…"
                      />
                    </div>
                  </div>
                </section>
                )}

                {/* Metadata */}
                <section className="bl-glass p-6">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                    Metadata
                  </h4>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <div>
                      <dt className="text-[0.7rem] text-gray-400 uppercase tracking-wider mb-0.5">Submitted</dt>
                      <dd className="text-gray-100">{formatDate(selected.submittedAt)}</dd>
                    </div>
                    <div>
                      <dt className="text-[0.7rem] text-gray-400 uppercase tracking-wider mb-0.5">Updated</dt>
                      <dd className="text-gray-100">{formatDate(selected.updatedAt)}</dd>
                    </div>
                    {selected.archivedAt ? (
                      <div>
                        <dt className="text-[0.7rem] text-gray-400 uppercase tracking-wider mb-0.5">Archived</dt>
                        <dd className="text-gray-100">{formatDate(selected.archivedAt)}</dd>
                      </div>
                    ) : null}
                    {/* No IP field: submitter IPs are never logged (privacy policy 2026-07-06) */}
                    <div className="sm:col-span-2">
                      <dt className="text-[0.7rem] text-gray-400 uppercase tracking-wider mb-0.5">User agent</dt>
                      <dd className="text-gray-300 break-all text-xs leading-relaxed">{selected.userAgent || "—"}</dd>
                    </div>
                  </dl>
                </section>
              </div>

              {/* Sticky footer */}
              <div
                className="sticky bottom-0 px-6 py-4 flex items-center justify-between gap-3 flex-wrap"
                style={{
                  background: "rgba(14, 13, 41, 0.92)",
                  backdropFilter: "blur(14px)",
                  WebkitBackdropFilter: "blur(14px)",
                  borderTop: "1px solid var(--bl-line)",
                }}
              >
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-3 py-2 text-sm font-medium text-red-400 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-50"
                  style={{ visibility: canDelete ? "visible" : "hidden" }}
                >
                  {deleting ? "Deleting…" : "Delete"}
                </button>
                <div className="flex items-center gap-2">
                  {canCreateListing && selected.type === "token" && (
                    <button
                      onClick={() => setListingPrefill(submissionToForm(selected))}
                      className="px-3 py-2 text-sm font-medium text-gray-200 border border-white/10 hover:bg-white/[0.04] hover:border-white/20 rounded-md transition-colors"
                    >
                      Create listing
                    </button>
                  )}
                  {canUpdate && (
                    <button
                      onClick={handleArchiveToggle}
                      disabled={archiving}
                      className="px-3 py-2 text-sm font-medium text-gray-200 border border-white/10 hover:bg-white/[0.04] hover:border-white/20 rounded-md transition-colors disabled:opacity-50"
                    >
                      {archiving
                        ? selected.archivedAt ? "Unarchiving…" : "Archiving…"
                        : selected.archivedAt ? "Unarchive" : "Archive"}
                    </button>
                  )}
                  <button
                    onClick={closeDrawer}
                    className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-gray-100 hover:bg-white/[0.04] rounded-md transition-colors"
                  >
                    Close
                  </button>
                  {canUpdate && (
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 text-sm font-medium bg-bl-accent text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 shadow-sm"
                    >
                      {saving ? "Saving…" : "Save changes"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Token listing form, prefilled from a token submission */}
      {canCreateListing && (
        <ListingForm
          open={Boolean(listingPrefill)}
          mode="create"
          initialForm={listingPrefill ? listingPrefill.form : null}
          hints={listingPrefill ? listingPrefill.hints : []}
          onClose={() => setListingPrefill(null)}
          onSaved={(listing) => {
            setListingPrefill(null);
            showToast(
              "success",
              listing && listing.status === "active" ? "Listing created and active." : "Listing created as a draft. Open Token Listings to review it.",
            );
          }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-[1100] max-w-[min(22rem,calc(100vw-3rem))]">
          <div
            className={`px-4 py-3 rounded-md shadow-lg text-sm border break-words ${
              toast.kind === "success"
                ? "bg-green-500/12 border-green-500/30 text-green-300"
                : toast.kind === "error"
                ? "bg-red-500/12 border-red-500/30 text-red-300"
                : "bg-bl-accent/8 border-gray-700 text-gray-200"
            }`}
          >
            {toast.text}
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className="bl-glass px-4 py-3 min-w-0">
    <div className="flex items-center gap-1.5 text-xs text-gray-400 min-w-0">
      {color && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />}
      <span className="truncate">{label}</span>
    </div>
    <div
      className="text-xl font-medium text-gray-100 mt-1 tabular-nums truncate"
      title={typeof value === "number" ? value.toLocaleString() : String(value)}
    >
      {typeof value === "number" ? value.toLocaleString() : value}
    </div>
  </div>
);

export default FormSubmissions;
